import type HlsType from 'hls.js'
import { create } from 'zustand'
import { savePosition, streamUrl } from '@/entities/soundscape'

const VOLUME_KEY = 'focus.soundscape.volume'
const LAST_KEY = 'focus.soundscape.last'
const GROUP_KEY = 'focus.soundscape.group'
const DEFAULT_VOLUME = 0.6

// Плеер живёт вне React-дерева: уход со страницы фокуса на доску не должен
// обрывать звук. Отсюда же — синглтоны <audio> и Hls, а не ref в компоненте.
let audio: HTMLAudioElement | null = null
let hls: HlsType | null = null

// hls.js — почти 400 КБ, и он не нужен, пока звук не включили (а в Safari не
// нужен вовсе). Поэтому грузим его первым нажатием, а не вместе со страницей.
async function loadHls(): Promise<typeof HlsType> {
  const mod = await import('hls.js')
  return mod.default
}

// Как часто точка прослушивания уезжает на сервер, пока идёт звук. Это отметка
// «докуда дослушал», а не телеметрия: реже — дешевле, а потерять можно максимум
// две минуты, если вкладку закрыли жёстко.
const SAVE_EVERY_MS = 120_000

function element(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio()
    audio.preload = 'none'
    audio.volume = readVolume()
    // Позиция и длина живут в сторе: таймлайн рисуется от них, а плеер
    // остаётся синглтоном вне React-дерева.
    audio.addEventListener('timeupdate', () => {
      const next = Math.floor(audio?.currentTime ?? 0)
      if (next !== Math.floor(useSoundscapePlayer.getState().position)) {
        useSoundscapePlayer.setState({ position: next })
      }
    })
    audio.addEventListener('durationchange', () => {
      const d = audio?.duration ?? 0
      useSoundscapePlayer.setState({ duration: Number.isFinite(d) ? d : 0 })
    })
  }
  return audio
}

// Точку сохраняем по паузе, смене режима и раз в SAVE_EVERY_MS: возвращаться
// «примерно туда» достаточно, а писать на каждый тик — незачем.
let saveTimer: number | null = null

function flushPosition(slug: string | null, position: number) {
  if (!slug || position <= 0) return
  void savePosition(slug, position).catch(() => {
    // Не дошло — не беда: следующая пауза или тик повторят. Ронять на этом
    // звук нельзя, он тут главный.
  })
}

function startSaveTimer() {
  stopSaveTimer()
  if (typeof window === 'undefined') return
  saveTimer = window.setInterval(() => {
    const { slug, position, playing } = useSoundscapePlayer.getState()
    if (playing) flushPosition(slug, position)
  }, SAVE_EVERY_MS)
}

function stopSaveTimer() {
  if (saveTimer !== null) {
    clearInterval(saveTimer)
    saveTimer = null
  }
}

// Явная проверка на отсутствие ключа: `Number(null)` — это 0, а не NaN, и без
// неё первый же запуск играл бы на нулевой громкости, выглядя полностью рабочим.
function readVolume(): number {
  const stored = localStorage.getItem(VOLUME_KEY)
  if (stored === null) return DEFAULT_VOLUME
  const raw = Number(stored)
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : DEFAULT_VOLUME
}

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(key)
}

// Между режимами рвём поток целиком: HLS-манифест Endel персонален и на сутки
// вперёд, переиспользовать его для другого режима нечего.
function detach() {
  if (hls) {
    hls.destroy()
    hls = null
  }
  const el = element()
  el.pause()
  el.removeAttribute('src')
  el.load()
}

// Системный плеер ОС: название режима на локскрине и кнопки play/pause с
// клавиатуры. Поддержки может не быть — тогда просто пропускаем.
function publishMediaSession(title: string, onStop: () => void) {
  if (!('mediaSession' in navigator)) return
  navigator.mediaSession.metadata = new MediaMetadata({
    title,
    artist: 'Фокус · Endel',
    album: 'STIT Task',
  })
  navigator.mediaSession.setActionHandler('pause', onStop)
  navigator.mediaSession.setActionHandler('stop', onStop)
}

function clearMediaSession() {
  if (!('mediaSession' in navigator)) return
  navigator.mediaSession.metadata = null
}

interface PlayerState {
  slug: string | null
  title: string
  playing: boolean
  loading: boolean
  error: string | null
  volume: number
  // Последний включённый режим и открытый раздел каталога переживают перезагрузку:
  // «включить музыку» с карточки задачи возвращает то, под что человек работает,
  // а мини-плеер в шапке и страница фокуса смотрят на один и тот же раздел.
  lastSlug: string | null
  group: string | null
  // Где сейчас поток и сколько его всего (суточный микс — около 24 часов).
  // Отсюда рисуется таймлайн и туда же возвращает перемотка.
  position: number
  duration: number
  play: (slug: string, title: string, startAt?: number) => void
  toggle: (slug: string, title: string) => void
  stop: () => void
  seek: (seconds: number) => void
  setVolume: (v: number) => void
  setGroup: (key: string) => void
}

export const useSoundscapePlayer = create<PlayerState>((set, get) => ({
  slug: null,
  title: '',
  playing: false,
  loading: false,
  error: null,
  volume: typeof window === 'undefined' ? DEFAULT_VOLUME : readVolume(),
  lastSlug: readStored(LAST_KEY),
  group: readStored(GROUP_KEY),
  position: 0,
  duration: 0,

  play: (slug, title, startAt = 0) => {
    const el = element()
    // Уходя с режима, оставляем метку — иначе возврат к нему начинался бы с нуля.
    const prev = get()
    if (prev.slug && prev.slug !== slug) flushPosition(prev.slug, prev.position)

    detach()
    set({ slug, title, lastSlug: slug, loading: true, error: null, position: startAt, duration: 0 })
    localStorage.setItem(LAST_KEY, slug)
    publishMediaSession(title, () => get().stop())

    const url = streamUrl(slug)
    const start = () => {
      // Возврат в точку возможен только когда поток уже знает свою длину;
      // до неё currentTime молча игнорируется.
      if (startAt > 0 && Number.isFinite(el.duration) && startAt < el.duration) {
        el.currentTime = startAt
      }
      el.play().then(
        () => {
          set({ playing: true, loading: false, error: null })
          startSaveTimer()
        },
        // Автоплей до жеста пользователя браузер отклоняет — это не поломка
        // потока, но молча возвращённое «стоит» читается как сломанная кнопка.
        () =>
          set({
            playing: false,
            loading: false,
            error: 'Браузер заблокировал звук — нажмите ещё раз',
          }),
      )
    }

    // Safari играет HLS сам — там hls.js не нужен и даже не загружается.
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = url
      el.addEventListener('loadedmetadata', start, { once: true })
      el.load()
      return
    }

    loadHls().then(
      (Hls) => {
        // Пока грузился модуль, режим могли переключить или выключить — тогда
        // поднимать поток уже некому.
        if (get().slug !== slug) return
        if (!Hls.isSupported()) {
          set({ playing: false, loading: false, error: 'Браузер не поддерживает поток' })
          return
        }

        hls = new Hls({ enableWorker: true })
        hls.loadSource(url)
        hls.attachMedia(el)
        hls.on(Hls.Events.MANIFEST_PARSED, start)
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return
          // Фатальную сетевую/медиа-ошибку hls.js умеет пережить рестартом; всё
          // остальное — конец потока, честно говорим об этом.
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls?.startLoad()
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError()
          else set({ playing: false, loading: false, error: 'Поток прервался' })
        })
      },
      () => set({ playing: false, loading: false, error: 'Не удалось загрузить плеер' }),
    )
  },

  toggle: (slug, title) => {
    const { slug: current, playing, play, stop } = get()
    if (current === slug && playing) stop()
    else play(slug, title)
  },

  stop: () => {
    const { slug, position } = get()
    flushPosition(slug, position)
    stopSaveTimer()
    detach()
    clearMediaSession()
    set({
      slug: null,
      title: '',
      playing: false,
      loading: false,
      error: null,
      position: 0,
      duration: 0,
    })
  },

  seek: (seconds) => {
    const el = element()
    const target = Math.max(0, seconds)
    if (Number.isFinite(el.duration) && el.duration > 0) {
      el.currentTime = Math.min(target, el.duration)
    } else {
      el.currentTime = target
    }
    set({ position: el.currentTime })
    // Перемотка — осознанный выбор точки: запоминаем сразу, не дожидаясь тика.
    flushPosition(get().slug, el.currentTime)
  },

  setVolume: (v) => {
    element().volume = v
    localStorage.setItem(VOLUME_KEY, String(v))
    set({ volume: v })
  },

  setGroup: (key) => {
    localStorage.setItem(GROUP_KEY, key)
    set({ group: key })
  },
}))
